import { useState, useEffect, useCallback, useRef, useContext } from "react";
import Swal from 'sweetalert2';
import {
    Box,
    Typography,
    Stack,
    IconButton,
    Skeleton,
    Button,
    Backdrop,
    Paper
} from "@mui/material";
import QRCode from "qrcode";

import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { formatToLongDate } from "../../utils/formatToLongDate";
import { UserContext } from "../../context/UserContext";
import PaymentSummaryQR from "./PaymentSummaryQR";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const phpFmt = (n) => Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const apiUrl = process.env.REACT_APP_API_URL;

async function fetchPayments(orderID, customerID) {

    try {
        const response = await fetch(`${apiUrl}manage_payment/get_payment_history.php`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                custID: customerID,
                orderID,
            }),
        }
        );

        const result = await response.json();

        if (result.status === "success") {
            return {
                payments: result.payment_history || [],
                paymentStatus: result.payment_status?.PayStatus || "Pending",
            };
        }

        return {
            payments: [],
            paymentStatus: "Pending",
        };

    } catch (error) {

        console.error(error);

        return {
            payments: [],
            paymentStatus: "Pending",
        };
    }
}

// ─── Status configs ───────────────────────────────────────────────────────────
const ORDER_STATUS_CFG = {
    Arrived: { bg: "#D1FAE5", fg: "#065F46" },
    Confirmed: { bg: "#DBEAFE", fg: "#1E40AF" },
    Pending: { bg: "#FEF3C7", fg: "#92400E" },
    Shipped: { bg: "#EDE9FE", fg: "#4C1D95" },
};

const METHOD_CFG = {
    GCash: { bg: "#D1FAE5", fg: "#065F46" },
    BDO: { bg: "#DBEAFE", fg: "#1E40AF" },
    Maya: { bg: "#F3E5F5", fg: "#4A148C" },
    Cash: { bg: "#FEF9C3", fg: "#713F12" },
};


// ─── html2canvas export ───────────────────────────────────────────────────────
async function exportAsImage(el, filename) {
    if (!window.html2canvas) {
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
        });
    }
    const canvas = await window.html2canvas(el, {
        backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false,
    });

    const a = document.createElement("a");
    a.download = filename; a.href = canvas.toDataURL("image/png"); a.click();
}

// EXPORT SHARED QR IMAGE
async function exportAsSharedQr(el) {
    if (!el) {
        throw new Error("No element provided for export.");
    }

    if (!window.html2canvas) {
        await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
                "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
            script.onload = resolve;
            script.onerror = () =>
                reject(new Error("Failed to load html2canvas."));
            document.head.appendChild(script);
        });
    }

    const canvas = await window.html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
    });

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error("Failed to create PNG image."));
            }
        }, "image/png");
    });
}

// ─── Payments mini-table cell ─────────────────────────────────────────────────
function PaymentsCell({ open, orderID, customerID }) {

    const { paymentMethods } = useContext(UserContext);
    const [rows, setRows] = useState(null);

    const totalPaid = rows?.payments.reduce((sum, item) => sum + Number(item.PaidAmount), 0);

    useEffect(() => {

        let cancelled = false;

        fetchPayments(orderID, customerID).then((data) => { if (!cancelled) setRows(data); });

        return () => { cancelled = true; };

    }, [open, orderID, customerID]);

    if (rows?.payments === null) {
        return (
            <Stack spacing={0.5}>
                <Skeleton height={12} width="80%" />
                <Skeleton height={12} width="60%" />
            </Stack>
        );
    }

    if (rows?.payments.length === 0) {
        return (
            <span style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>
                No payments yet
            </span>
        );
    }

    return (
        <table style={{
            borderCollapse: "collapse",
            width: "100%",
            fontSize: 11,
        }}>
            <thead>
                <tr style={{ backgroundColor: "#F1F5F9" }}>
                    {["Paid Amount", "Method", "Date"].map((h) => (
                        <th key={h} style={{
                            padding: "4px 8px",
                            fontWeight: 700,
                            color: "#475569",
                            border: "1px solid #CBD5E1",
                            whiteSpace: "nowrap",
                            textAlign: "left",
                            fontSize: 10,
                        }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows?.payments.map((p, i) => {

                    const method = paymentMethods.find(
                        pm => pm.id === Number(p.PaymentMethod)
                    );

                    const mc = METHOD_CFG[method ? method.AccType : p.PaymentMethod] ??
                        { bg: "#F3F4F6", fg: "#374151" };

                    return (
                        <tr key={p.id} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#F8FAFC" }}>
                            <td
                                style={{
                                    padding: "4px 8px",
                                    border: "1px solid #E2E8F0",
                                    fontWeight: 700,
                                    color: "#059669",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                ₱{phpFmt(p.PaidAmount)}
                            </td>
                            <td style={{ padding: "4px 8px", border: "1px solid #E2E8F0" }}>
                                <span
                                    style={{
                                        display: "inline-block",
                                        padding: "1px 7px",
                                        borderRadius: 3,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        backgroundColor: mc.bg,
                                        color: mc.fg,
                                    }}
                                >
                                    {method ? method.AccType : p.PaymentMethod}
                                </span>
                            </td>
                            <td
                                style={{
                                    padding: "4px 8px",
                                    border: "1px solid #E2E8F0",
                                    color: "#64748B",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {formatToLongDate(p.PaymentDate)}
                            </td>
                        </tr>
                    );
                })}
                <tr style={{ paddingTop: 2 }}>
                    <td>
                        Total Paid: {totalPaid}
                    </td>
                </tr>
            </tbody>
        </table>
    );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────
export default function PaymentSummary({ open, onClose, orders = [] }) {

    const printRef = useRef(null);
    const [exporting, setExporting] = useState(false);

    const [qrImage, setQrImage] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [openQrImg, setOpenQrImg] = useState(false);
    const [exportingQrImg, setExportingQrImg] = useState(false);

    const customer = orders[0];

    let running = 0;

    const ordersWithRunning = orders.map((o) => {
        const total = (Number(o.Price) * Number(o.ItemQty)) + Number(o.SFee);
        running += total;

        return { ...o, _total: total, _running: total };
    });

    const totalAmount = ordersWithRunning.reduce((s, o) => s + Number(o._running), 0);
    const totalBalance = orders.reduce((s, o) => s + Number(o.ItemBalance), 0);
    const totalPaidOnOrders = orders.reduce((s, o) => s + Number(o.PaidAmount), 0);

    // EXPORT AS IMAGE 
    const handleExport = useCallback(async () => {
        if (!printRef.current) return;

        setExporting(true);

        await exportAsImage(
            printRef.current,
            `payment-summary-${customer?.CustomerID ?? "export"}.png`
        );

        setExporting(false);
    }, [customer]);

    // GENERATE QR IMG
    const handleExportQrImg = useCallback(async () => {
        if (!printRef.current) return;

        setExportingQrImg(true);

        try {
            // 1. Convert the component into a PNG Blob
            const blob = await exportAsSharedQr(printRef.current);

            // 2. Upload the temporary image
            const formData = new FormData();
            formData.append(
                "image",
                blob,
                `payment-summary-${customer?.CustomerID ?? "export"}.png`
            );

            const response = await fetch(
                `${apiUrl}shared_text/upload_temp_image.php`,
                {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || "Image upload failed.");
            }

            // 3. Create the public page URL
            const imagePageUrl = new URL(
                `/copy-image/${result.token}`,
                window.location.origin
            ).href;

            // 4. Generate QR code
            const qrDataUrl = await QRCode.toDataURL(imagePageUrl, {
                width: 400,
                margin: 2,
                errorCorrectionLevel: "M",
            });

            // 5. Show QR code in your UI
            setQrImage(qrDataUrl);
            setImageUrl(imagePageUrl);
            setOpenQrImg(true);

        } catch (error) {
            console.error("Export failed:", error);
            alert(error.message || "Failed to export and share image.");
        } finally {
            setExportingQrImg(false);
        }
    }, [customer, apiUrl]);

    // ── shared table cell styles (inline, so html2canvas captures them) ──────
    const TH = {
        padding: "8px 10px",
        fontWeight: 700,
        fontSize: 11,
        color: "#334155",
        backgroundColor: "#EFF2F7",
        border: "1px solid #CBD5E1",
        textAlign: "left",
        whiteSpace: "nowrap",
        letterSpacing: "0.3px",
    };

    const TD = {
        padding: "10px 10px",
        fontSize: 11,
        color: "#1E293B",
        border: "1px solid #CBD5E1",
        verticalAlign: "top",
    };

    const TD_RIGHT = { ...TD, textAlign: "right" };
    const TD_CENTER = { ...TD, textAlign: "center" };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Paper
                sx={{
                    width: "95%",
                    maxWidth: 1400,
                    maxHeight: "92vh",
                    borderRadius: 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,.14)",
                    display: "flex",
                    flexDirection: "column",

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                {/* ── Top bar ── */}
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 2.5, py: 1.5,
                    borderBottom: "1px solid #CBD5E1",
                    bgcolor: "#fff",
                    flexShrink: 0,
                }}>
                    <Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                            Payment Summary
                        </Typography>
                        {customer && (
                            <Typography sx={{ fontSize: 12, color: "#64748B", mt: "1px" }}>
                                {customer.CustomerName.trim()}&nbsp;·&nbsp;
                                <span style={{ fontFamily: "monospace" }}>{customer.CustomerID}</span>
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleExportQrImg}
                            disabled={exportingQrImg}
                            sx={{
                                bgcolor: "#1E3A5F", "&:hover": { bgcolor: "#162D4A" },
                                fontSize: 12, fontWeight: 600, textTransform: "none",
                                px: 2, py: 0.65, borderRadius: "5px", boxShadow: "none",
                            }}
                        >
                            {exportingQrImg ? "Preparing Qr…" : "Scan Copy"}
                        </Button>

                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                            onClick={handleExport}
                            disabled={exporting}
                            sx={{
                                bgcolor: "#1E3A5F", "&:hover": { bgcolor: "#162D4A" },
                                fontSize: 12, fontWeight: 600, textTransform: "none",
                                px: 2, py: 0.65, borderRadius: "5px", boxShadow: "none",
                            }}
                        >
                            {exporting ? "Exporting…" : "Export as Image"}
                        </Button>

                        <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>

                {/* ── Scrollable body ── */}
                <Box sx={{ overflowY: "auto", flex: 1, bgcolor: "#F8FAFC", p: 2.5 }}>
                    <Box
                        ref={printRef}
                        sx={{ bgcolor: "#fff", p: 3, borderRadius: "6px", border: "1px solid #CBD5E1" }}
                    >
                        {/* Customer header */}
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
                                {customer?.CustomerName.trim()}
                            </Typography>
                        </Box>

                        {/* ── MAIN TABLE ── */}
                        <table style={{
                            borderCollapse: "collapse",
                            width: "100%",
                            tableLayout: "auto",
                            marginBottom: 24,
                        }}>
                            <thead>
                                <tr>
                                    <th style={{ ...TH, width: 120 }}>DATE</th>
                                    <th style={{ ...TH }}>ITEM</th>
                                    <th style={{ ...TH, width: 48, textAlign: "center" }}>QTY</th>
                                    <th style={{ ...TH, width: 90, textAlign: "right" }}>PRICE</th>
                                    <th style={{ ...TH, width: 75, textAlign: "right" }}>SF</th>
                                    <th style={{ ...TH, width: 300 }}>PAYMENTS</th>
                                    <th style={{ ...TH, width: 118, textAlign: "right" }}>RUNNING BALANCE</th>
                                    <th style={{ ...TH, width: 90 }}>STATUS</th>
                                    <th style={{ ...TH, width: 108, textAlign: "right" }}>BALANCE TO PAY</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ordersWithRunning.map((o, i) => {
                                    const rowBg = i % 2 === 0 ? "#ffffff" : "#F8FAFC";
                                    const hasBalance = Number(o.ItemBalance) > 0;
                                    const osCfg = ORDER_STATUS_CFG[o.OrderStat] ?? { bg: "#F3F4F6", fg: "#374151" };

                                    return (
                                        <tr key={o.id} style={{ backgroundColor: rowBg }}>
                                            {/* DATE */}
                                            <td style={{ ...TD, color: "#475569" }}>{formatToLongDate(o.DateOrdered)}</td>

                                            {/* ITEM */}
                                            <td style={TD}>
                                                <div style={{ fontWeight: 600, fontSize: 12, color: "#0F172A", lineHeight: 1.4 }}>
                                                    {o.ItemName}
                                                </div>
                                                <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>
                                                    {o.ItemCategory} · {o.Cargo}
                                                </div>
                                            </td>

                                            {/* QTY */}
                                            <td style={{ ...TD_CENTER, fontWeight: 600 }}>{o.ItemQty}</td>

                                            {/* PRICE */}
                                            <td style={TD_RIGHT}>{phpFmt(o.Price)}</td>

                                            {/* SF */}
                                            <td style={TD_RIGHT}>{phpFmt(o.SFee)}</td>

                                            {/* PAYMENTS — nested table */}
                                            <td style={TD}>
                                                <PaymentsCell
                                                    open={open}
                                                    orderID={o.OrderID}
                                                    customerID={o.CustomerID}
                                                />
                                            </td>

                                            {/* RUNNING BALANCE */}
                                            <td style={{ ...TD_RIGHT, fontWeight: 700 }}>
                                                {phpFmt(o._running)}
                                            </td>

                                            {/* STATUS */}
                                            <td style={TD}>
                                                <span style={{
                                                    display: "inline-block",
                                                    padding: "2px 8px",
                                                    borderRadius: 3,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    backgroundColor: osCfg.bg,
                                                    color: osCfg.fg,
                                                    whiteSpace: "nowrap",
                                                }}>
                                                    {o.OrderStat}
                                                </span>
                                            </td>

                                            {/* BALANCE TO PAY */}
                                            <td style={{
                                                ...TD_RIGHT,
                                                fontWeight: 700,
                                                color: hasBalance ? "#DC2626" : "#94A3B8",
                                            }}>
                                                {hasBalance ? phpFmt(o.ItemBalance) : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* ── FOOTER TOTALS ── */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>

                            {/* BALANCE TO PAY */}
                            <Stack style={{
                                padding: "12px 20px 10px",
                                backgroundColor: "#F8FAFC",
                                border: "1px solid #CBD5E1",
                            }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", letterSpacing: "0.8px", marginBottom: 3 }}>
                                    TOTAL ITEM AMOUNT
                                </div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: "#1E293B", fontFamily: "monospace" }}>
                                    ₱{phpFmt(totalAmount)}
                                </div>
                            </Stack>

                            {/* TOTAL PAYMENTS */}
                            <Stack style={{
                                padding: "12px 20px 10px",
                                backgroundColor: "#F0FDF4",
                                border: "1px solid #CBD5E1",
                            }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", letterSpacing: "0.8px", marginBottom: 3 }}>
                                    TOTAL PAYMENTS
                                </div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: "#059669", fontFamily: "monospace" }}>
                                    ₱{phpFmt(totalPaidOnOrders)}
                                </div>
                            </Stack>

                            {/* TOTAL BALANCE TO PAY */}
                            <Stack>
                                <Stack style={{
                                    padding: "14px 20px 14px",
                                    backgroundColor: "#FFF5F5",
                                    border: "1px solid #CBD5E1",
                                }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", letterSpacing: "0.8px", marginBottom: 3 }}>
                                        TOTAL BALANCE TO PAY
                                    </div>
                                    <div style={{ fontSize: 16, fontWeight: 900, color: "#DC2626", fontFamily: "monospace" }}>
                                        ₱{phpFmt(totalBalance)}
                                    </div>
                                </Stack>
                            </Stack>

                        </Box>
                    </Box>
                </Box>

                <PaymentSummaryQR
                    open={openQrImg}
                    onClose={() => setOpenQrImg(false)}
                    qrImage={qrImage}
                    imageUrl={imageUrl}
                />

            </Paper>

        </Backdrop>
    );
}